<template>
<div v-if="hasContent || typevalue =='product'">
  <section :id="getId(id,'section')" :class="typevalue =='product' ? 'pl-0 pr-0 pb-0 lg:pr-[2.142857rem] lg:pb-[0.75rem]' :'lg:py-[4rem] lg:pr-[2.142857rem] py-[2rem] px-6 lg:pl-0 lg:scroll-mt-35 scroll-mt-55'">
    <Heading :id="getId(id,'heading')" v-if="typevalue =='product' && heading" :level="4" color="text-ash-200 mr-[-2.142857rem] pb-[1.071428rem]">
      {{ heading }}
    </Heading>
    <Heading v-else-if="heading" :id="getId(id,'heading')" :level="4" className="text-[1.2857142857rem] pb-[1.0714285714rem] leading-[1.125] font-medium" color="text-ash-200">{{`${heading} Details`}}</Heading>
    
    <hr class="border-t border-gray-300" aria-hidden="true">
    <div class="block"> 
      <div
       ref="wrapper"
          :style="
            typevalue === 'product' && !expanded && collapsedHeight
              ? { maxHeight: collapsedHeight + 'px' }
              : {}
          "
       :class="{
          'relative after:absolute after:bottom-0 after:left-0 after:h-full after:w-full after:content-[] after:bg-[linear-gradient(to_top,_white_20%,_transparent_80%)] overflow-hidden is-collapsed': !expanded && showMoreBtn,
          'h-[14.286rem] mt-[1rem]': !expanded && typevalue !== 'product' && showMoreBtn,
          'my-[1rem]': expanded
        }"
      >
        <div :id="getId(id,'description')" tabindex="-1" class="content transcript-product font-normal text-ash-100 text-[1rem] leading-[1.5] pdp-description">
          <slot name="description">
              <div class="t5d-rich-text" v-html="description"></div>
            </slot>
        </div>
      </div>
      <div  v-if="showMoreBtn"
          class="flex justify-center showmore"
          :class="{
            'relative -top-[0.714rem]': !expanded,
            'relative -top-0': expanded,
          }">
        <Button
          class="button is-large show-more-less text-ash-500"
          @click="toggle"
          :aria-expanded="expanded"
          :aria-controls="getId(id,'section')"
          @keydown.enter.prevent="toggle"
          @keydown.space.prevent="toggle"
          variant="secondary"
          theme="LIGHT"
          size="sm"
          :id="`button-${id}`"
        >
          {{ expanded ? 'Show Less' : 'Show More' }}
        </Button>
      </div>
    </div>
  </section>
  <hr v-if="typevalue !== 'product'" class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true">
</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch, useSlots } from 'vue'
import Heading from "../atom/heading.vue";
import Button from "../atom/button.vue";
import { extractTextFromHtml } from "#shared/utils/common";
import { useStickyTabs } from "~/composables/useNavSticky";

const props = defineProps({
  heading: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: ""
  },
  typevalue :{
    type:String,
    default:'takefivedaily'
  },
  showMoreBtn: {
    type: Boolean,
    default: true
  },
  expanded: {
    type: Boolean,
    default: false
  }
});

const expanded = ref(props.expanded);
const wrapper = ref<HTMLElement | null>(null);
const collapsedHeight = ref(0);
const { expandedSections } = useStickyTabs()

// Toggles the expanded state of a table section and manages keyboard focus for accessibility when new content is rendered from builderio.
const toggle = async() => {
  expanded.value = !expanded.value
  const sectionId = getId(props.id, 'section')
  const expandedSectionKeyMap: Record<string, string> = {
    'transcript-ingredient-infosection': 'ingredients'
  }
const expandedKey =
  expandedSectionKeyMap[sectionId] || sectionId
  if (!expanded.value) {
     expandedSections.value[expandedKey] = false
  }
  if (!wrapper.value) return;

  const firstCell = wrapper?.value.querySelector("tbody tr:nth-child(2) td:nth-child(1)") as HTMLElement;
  if (!firstCell) return;

  const nextCell = wrapper?.value.querySelector("tbody tr:nth-child(2) td:nth-child(2)") as HTMLElement;

  if (expanded.value) {
    await nextTick();
    requestAnimationFrame(() => {
      firstCell.setAttribute('tabindex','0');
      firstCell.focus({ preventScroll: true });
      if(nextCell) {
        nextCell.setAttribute('tabindex', '0');
        const handleTab = (e: KeyboardEvent) => {
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault(); 
            nextCell.focus({ preventScroll: true });  
            firstCell.removeEventListener('keydown', handleTab);
          }
        };
        firstCell.addEventListener('keydown', handleTab);
      }
    });
  } else {
    firstCell?.removeAttribute('tabindex');
    nextCell?.removeAttribute('tabindex');
  }
}

const slots = useSlots()
watch(() => props.expanded, (newValue) => {
  expanded.value = newValue
})

const hasContent = computed(() => {
  if (slots.description?.().length) {
    return true
  }
  const text = extractTextFromHtml(props.description ?? "");
  return text.length > 0;
});

onMounted(async () => {
  await nextTick();
  requestAnimationFrame(() => { 
    if (!wrapper.value) return;
    const firstRow = wrapper.value.querySelector("tbody tr") as HTMLElement;
    if (firstRow) {
      const wrapperTop = wrapper.value.getBoundingClientRect().top;
      const rowBottom = firstRow.getBoundingClientRect().bottom;
      collapsedHeight.value = rowBottom - wrapperTop + 30;
    }
  })    
});

const getId = (id?: string | number, sectionName  = 'section') => {
  if (id) {
    return 'transcript-' + id + sectionName
  } else {
    return 'transcript-' + sectionName
  }
}

</script>

<style scoped>
.t5d-rich-text :deep(ul) {
  list-style-type: disc;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.t5d-rich-text :deep(ol) {
  list-style-type: decimal;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.t5d-rich-text :deep(li) {
  margin: 0.25rem 0;
}

.t5d-rich-text :deep(hr) {
  border: 0;
  border-top: 1px solid #d1d5db;
  margin: 1.5rem 0;
}

.t5d-rich-text :deep(p),
.t5d-rich-text :deep(h1),
.t5d-rich-text :deep(h2),
.t5d-rich-text :deep(h3),
.t5d-rich-text :deep(h4),
.t5d-rich-text :deep(h5),
.t5d-rich-text :deep(h6),
.t5d-rich-text :deep(blockquote),
.t5d-rich-text :deep(table),
.t5d-rich-text :deep(ul),
.t5d-rich-text :deep(ol),
.t5d-rich-text :deep(hr) {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>
